/**
 * Activity Logs Page - Owner only
 * Monitor user activities and detect fraud
 */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import {
  getActivityLogs,
  getSuspiciousActivities,
  getActivitySummary,
  getUserActivities,
  ActivityLog,
  ActivitySummary,
  ActivityLogParams
} from '../../services/activityLogService';
import { getUsers, User } from '../../services/userService';
import { getBranches } from '../../services/branchService';
import { Branch } from '../../types/branch';

const ActivityLogsPage: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [suspiciousLogs, setSuspiciousLogs] = useState<ActivityLog[]>([]);
  const [summary, setSummary] = useState<ActivitySummary | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterUserId, setFilterUserId] = useState<number | undefined>(undefined);
  const [filterBranchId, setFilterBranchId] = useState<number | undefined>(undefined);
  const [filterActivityType, setFilterActivityType] = useState<string>('');
  const [filterSearch, setFilterSearch] = useState<string>('');
  const [summaryDays, setSummaryDays] = useState<number>(7);

  // View mode
  const [viewMode, setViewMode] = useState<'all' | 'suspicious' | 'summary'>('all');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (viewMode === 'all') {
      loadActivityLogs();
    } else if (viewMode === 'suspicious') {
      loadSuspiciousActivities();
    } else if (viewMode === 'summary') {
      loadSummary();
    }
  }, [viewMode, filterUserId, filterBranchId, filterActivityType, filterSearch, summaryDays]);

  const loadInitialData = async () => {
    try {
      const [usersData, branchesData] = await Promise.all([
        getUsers(),
        getBranches(),
      ]);
      setUsers(usersData);
      setBranches(branchesData);
    } catch (err: any) {
      console.error('Failed to load initial data:', err);
    }
  };

  const loadActivityLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: ActivityLogParams = {
        user_id: filterUserId,
        branch_id: filterBranchId,
        activity_type: filterActivityType || undefined,
        search: filterSearch || undefined,
        page_size: 100,
      };
      const data = await getActivityLogs(params);
      setLogs(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Gagal memuat activity logs');
    } finally {
      setLoading(false);
    }
  };

  const loadSuspiciousActivities = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSuspiciousActivities(summaryDays);
      setSuspiciousLogs(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Gagal memuat suspicious activities');
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getActivitySummary(summaryDays);
      setSummary(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Gagal memuat summary');
    } finally {
      setLoading(false);
    }
  };

  const getActivityTypeColor = (type: string) => {
    if (type.includes('failed') || type.includes('void')) {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    if (type.includes('login')) {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    }
    if (type.includes('create') || type.includes('add')) {
      return 'bg-green-100 text-green-800 border-green-200';
    }
    if (type.includes('update')) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
    if (type.includes('delete') || type.includes('reset')) {
      return 'bg-orange-100 text-orange-800 border-orange-200';
    }
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('id-ID', {
      dateStyle: 'short',
      timeStyle: 'medium'
    });
  };

  if (!currentUser || currentUser.role !== 'owner') {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600">Access denied. Owner only.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">📊 Activity Logs</h1>
          <p className="text-gray-600">Monitor aktivitas karyawan dan deteksi fraud</p>
        </div>

        {/* View Mode Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setViewMode('all')}
              className={`px-6 py-3 font-medium ${
                viewMode === 'all'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              📋 Semua Aktivitas
            </button>
            <button
              onClick={() => setViewMode('suspicious')}
              className={`px-6 py-3 font-medium ${
                viewMode === 'suspicious'
                  ? 'border-b-2 border-red-600 text-red-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              ⚠️ Suspicious Activities
            </button>
            <button
              onClick={() => setViewMode('summary')}
              className={`px-6 py-3 font-medium ${
                viewMode === 'summary'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              📈 Summary
            </button>
          </div>
        </div>

        {/* Filters (for 'all' view) */}
        {viewMode === 'all' && (
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">User</label>
                <select
                  value={filterUserId || ''}
                  onChange={(e) => setFilterUserId(e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Semua User</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.username} - {user.full_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Cabang</label>
                <select
                  value={filterBranchId || ''}
                  onChange={(e) => setFilterBranchId(e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Semua Cabang</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.id}>
                      {branch.code} - {branch.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Activity Type</label>
                <select
                  value={filterActivityType}
                  onChange={(e) => setFilterActivityType(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Semua Type</option>
                  <option value="login">Login</option>
                  <option value="failed_login">Failed Login</option>
                  <option value="create_sale">Transaksi Penjualan</option>
                  <option value="void_sale">Void Transaksi</option>
                  <option value="create_item">Tambah Item</option>
                  <option value="update_item">Update Item/Harga</option>
                  <option value="create_user">Buat User</option>
                  <option value="update_user">Update User</option>
                  <option value="reset_password">Reset Password</option>
                  <option value="add_stock">Tambah Stock</option>
                  <option value="reduce_stock">Kurang Stock</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Search</label>
                <input
                  type="text"
                  value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                  placeholder="Cari action..."
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
          </div>
        )}

        {/* Summary Period Filter */}
        {(viewMode === 'suspicious' || viewMode === 'summary') && (
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <label className="block text-sm font-medium mb-2">Period:</label>
            <div className="flex gap-2">
              {[7, 14, 30, 90].map(days => (
                <button
                  key={days}
                  onClick={() => setSummaryDays(days)}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    summaryDays === days
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {days} hari
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Loading...</p>
          </div>
        ) : (
          <>
            {/* All Activities View */}
            {viewMode === 'all' && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">Time</th>
                        <th className="px-4 py-3 text-left font-semibold">User</th>
                        <th className="px-4 py-3 text-left font-semibold">Type</th>
                        <th className="px-4 py-3 text-left font-semibold">Action</th>
                        <th className="px-4 py-3 text-left font-semibold">Status</th>
                        <th className="px-4 py-3 text-left font-semibold">IP</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {logs.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap">
                            {formatDate(log.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            {log.user ? (
                              <div>
                                <div className="font-medium">{log.user.username}</div>
                                <div className="text-xs text-gray-500">{log.user.full_name}</div>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium border ${getActivityTypeColor(log.activity_type)}`}>
                              {log.activity_type}
                            </span>
                          </td>
                          <td className="px-4 py-3">{log.action}</td>
                          <td className="px-4 py-3">
                            {log.success ? (
                              <span className="text-green-600">✓ Success</span>
                            ) : (
                              <span className="text-red-600">✗ Failed</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {log.ip_address || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {logs.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    Tidak ada activity log ditemukan
                  </div>
                )}
              </div>
            )}

            {/* Suspicious Activities View */}
            {viewMode === 'suspicious' && (
              <div className="space-y-4">
                {suspiciousLogs.length === 0 ? (
                  <div className="bg-white rounded-lg shadow p-12 text-center">
                    <p className="text-gray-500">✓ Tidak ada suspicious activities</p>
                  </div>
                ) : (
                  suspiciousLogs.map((log) => (
                    <div key={log.id} className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium border ${getActivityTypeColor(log.activity_type)}`}>
                              {log.activity_type}
                            </span>
                            <span className="text-sm text-gray-500">{formatDate(log.created_at)}</span>
                          </div>
                          <p className="font-medium text-gray-800 mb-1">{log.action}</p>
                          {log.user && (
                            <p className="text-sm text-gray-600">
                              User: {log.user.username} ({log.user.full_name})
                            </p>
                          )}
                          {log.ip_address && (
                            <p className="text-xs text-gray-500">IP: {log.ip_address}</p>
                          )}
                          {log.error_message && (
                            <p className="text-sm text-red-600 mt-2">Error: {log.error_message}</p>
                          )}
                        </div>
                        {!log.success && (
                          <span className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm font-medium">
                            Failed
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Summary View */}
            {viewMode === 'summary' && summary && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-2">Total Activities</h3>
                  <p className="text-3xl font-bold text-gray-800">{summary.total_activities}</p>
                  <p className="text-sm text-gray-500 mt-1">dalam {summary.period_days} hari terakhir</p>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-2">Failed Activities</h3>
                  <p className="text-3xl font-bold text-red-600">{summary.failed_activities}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {summary.total_activities > 0 
                      ? `${((summary.failed_activities / summary.total_activities) * 100).toFixed(1)}% dari total`
                      : 'No data'}
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-2">Suspicious Activities</h3>
                  <p className="text-3xl font-bold text-orange-600">{summary.suspicious_activities}</p>
                  <p className="text-sm text-gray-500 mt-1">perlu perhatian khusus</p>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-2">Unique Users</h3>
                  <p className="text-3xl font-bold text-blue-600">{summary.unique_users}</p>
                  <p className="text-sm text-gray-500 mt-1">user aktif</p>
                </div>

                <div className="bg-white rounded-lg shadow p-6 md:col-span-2">
                  <h3 className="text-sm font-medium text-gray-600 mb-2">Most Active User</h3>
                  {summary.most_active_user ? (
                    <>
                      <p className="text-2xl font-bold text-gray-800">
                        {summary.most_active_user.full_name}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        @{summary.most_active_user.username}
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        {summary.most_active_count} aktivitas
                      </p>
                    </>
                  ) : (
                    <p className="text-gray-500">No data</p>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ActivityLogsPage;

